import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  EmailDetail,
  EmailDirection,
  EmailStatus,
  EmailSummary,
  PageResult,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { EmailStatusBadge, InjectionBadge } from "@/components/EmailStatusBadge";
import EmailReviewer from "@/components/EmailReviewer";
import ThreadHistory from "@/components/ThreadHistory";
import { formatDateTime } from "@/lib/format";
import { usePersistedState } from "@/lib/persistedState";
import FilterPresets from "@/components/FilterPresets";
import { cn } from "@/lib/cn";

const STATUSES: EmailStatus[] = [
  "DRAFT", "QUEUED", "SENT", "DELIVERED", "OPENED", "REPLIED",
  "BOUNCED", "COMPLAINED", "FAILED", "UNSUBSCRIBED", "REJECTED",
];

const DIRECTIONS: EmailDirection[] = ["OUTBOUND", "INBOUND"];

const PAGE_SIZE = 25;

/**
 * The filter selections we hand back to a returning user. Deliberately the
 * three dropdowns only: the search box takes free text that can name a
 * prospect, and prospect data does not get written to a viewer's disk.
 */
type StoredFilters = {
  strategyId: string;
  direction: EmailDirection | "";
  status: EmailStatus | "";
};

const DEFAULT_FILTERS: StoredFilters = { strategyId: "", direction: "", status: "" };

/**
 * Storage can hold anything — a payload from an older build, a value a user
 * edited by hand, an option since removed from the enum. Anything we cannot
 * render as a selected option becomes "All", so a bad key can never wedge the
 * page into a filter the UI has no way to show or clear.
 */
function reviveFilters(stored: unknown): StoredFilters | null {
  if (typeof stored !== "object" || stored === null) return null;
  const raw = stored as Record<string, unknown>;
  const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | "" =>
    typeof value === "string" && (allowed as readonly string[]).includes(value)
      ? (value as T)
      : "";
  return {
    strategyId: typeof raw.strategyId === "string" ? raw.strategyId : "",
    direction: oneOf(raw.direction, DIRECTIONS),
    status: oneOf(raw.status, STATUSES),
  };
}

/**
 * Every message the agent has sent or received, across all prospects.
 *
 * <p>The per-prospect thread view answers "what did we say to this one".
 * This answers "what has the agent been doing" — the view needed to supervise
 * an autonomous system rather than audit a single conversation. Selecting a row
 * opens the full message, with review controls when it is awaiting approval and
 * feedback capture always, and the rest of its thread beneath it — a message is
 * judged against what it answers, not on its own.
 *
 * <p>The dropdown filters persist across visits: supervising a queue means
 * coming back to the same slice ("everything awaiting approval") many times a
 * day, and re-picking it each time is the whole friction.
 */
export default function EmailsPage() {
  const [filters, setFilters] = usePersistedState(
    "emails:filters",
    DEFAULT_FILTERS,
    reviveFilters,
  );
  const { strategyId, direction, status } = filters;
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  // ?message=<id> opens one specific message — the landing point for links from
  // elsewhere in the CRM, notably the error overview. It seeds the selection
  // rather than filtering the list: the reviewer fetches by id, so the linked
  // message opens even when the remembered filters would have hidden its row.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get("message");
  const [selectedId, setSelectedId] = useState<string | null>(linkedId);

  useEffect(() => {
    if (linkedId) setSelectedId(linkedId);
  }, [linkedId]);

  // Picking a different message makes the deep link stale; drop it so a reload
  // or a back-navigation does not snap the pane back to the linked one.
  function selectMessage(id: string) {
    setSelectedId(id);
    if (searchParams.has("message")) {
      const next = new URLSearchParams(searchParams);
      next.delete("message");
      setSearchParams(next, { replace: true });
    }
  }

  const strategies = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const params = new URLSearchParams();
  if (strategyId) params.set("strategyId", strategyId);
  if (direction) params.set("direction", direction);
  if (status) params.set("status", status);
  if (search) params.set("q", search);
  params.set("page", String(page));
  params.set("size", String(PAGE_SIZE));

  const emails = useQuery({
    queryKey: ["emails", params.toString()],
    queryFn: () => api.get<PageResult<EmailSummary>>(`/api/v1/emails?${params.toString()}`),
  });

  const total = emails.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  function applyFilters(patch: Partial<StoredFilters>) {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }

  // A remembered strategy can be deleted between visits. Left in place it
  // renders as a blank dropdown that silently filters every message away.
  const strategyList = strategies.data;
  useEffect(() => {
    if (!strategyId || !strategyList) return;
    if (!strategyList.some((s) => s.id === strategyId)) {
      setFilters((prev) => ({ ...prev, strategyId: "" }));
      setPage(0);
    }
  }, [strategyId, strategyList, setFilters]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Emails</h1>
        <p className="text-sm text-[var(--fg-muted)]">
          Everything the agent has sent and received, across every prospect.
        </p>
      </header>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs text-[var(--fg-muted)]">Strategy</span>
            <select
              value={strategyId}
              onChange={(e) => applyFilters({ strategyId: e.target.value })}
              className="mt-1 rounded-md border border-[var(--border)] p-1.5 text-sm"
            >
              <option value="">All</option>
              {strategies.data?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs text-[var(--fg-muted)]">Direction</span>
            <select
              value={direction}
              onChange={(e) => applyFilters({ direction: e.target.value as EmailDirection | "" })}
              className="mt-1 rounded-md border border-[var(--border)] p-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="OUTBOUND">Sent by agent</option>
              <option value="INBOUND">Replies received</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="block text-xs text-[var(--fg-muted)]">Status</span>
            <select
              value={status}
              onChange={(e) => applyFilters({ status: e.target.value as EmailStatus | "" })}
              className="mt-1 rounded-md border border-[var(--border)] p-1.5 text-sm"
            >
              <option value="">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s === "DRAFT" ? "AWAITING APPROVAL" : s}</option>
              ))}
            </select>
          </label>

          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(q.trim());
              setPage(0);
            }}
          >
            <label className="text-sm">
              <span className="block text-xs text-[var(--fg-muted)]">Search</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Subject or body"
                className="mt-1 rounded-md border border-[var(--border)] p-1.5 text-sm"
              />
            </label>
            <button
              type="submit"
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50"
            >
              Apply
            </button>
          </form>

          <div className="ml-auto">
            <FilterPresets<StoredFilters>
              storageKey="emails"
              current={filters}
              revive={reviveFilters}
              onApply={(next) => { setFilters(next); setPage(0); }}
            />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4">
        <Card>
          <CardTitle>
            {total} message{total === 1 ? "" : "s"}
          </CardTitle>
          {emails.isLoading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : emails.data && emails.data.items.length > 0 ? (
            <>
              <ul className="divide-y divide-[var(--border)]">
                {emails.data.items.map((m) => (
                  <li key={m.id}>
                    <button
                      onClick={() => selectMessage(m.id)}
                      className={cn(
                        "w-full rounded-md p-3 text-left hover:bg-zinc-50",
                        selectedId === m.id && "bg-zinc-100",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{m.subject}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          {m.injectionSuspected ? <InjectionBadge /> : null}
                          <EmailStatusBadge status={m.status} />
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-[var(--fg-muted)]">
                        {m.prospectName ?? m.prospectEmail ?? "unknown prospect"}
                        {m.phaseSlug ? ` · ${m.phaseSlug}` : ""} ·{" "}
                        {formatDateTime(m.sentAt ?? m.receivedAt ?? m.createdAt)}
                        {m.edited ? " · edited" : ""}
                      </div>
                      <div className="mt-1 truncate text-xs text-zinc-600">{m.bodyPreview}</div>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex items-center justify-between text-sm">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-xs text-[var(--fg-muted)]">
                  Page {page + 1} of {lastPage + 1}
                </span>
                <button
                  type="button"
                  disabled={page >= lastPage}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md border border-[var(--border)] px-3 py-1 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--fg-muted)]">
              No messages match these filters.
            </div>
          )}
        </Card>

        <Card>
          <CardTitle>Message</CardTitle>
          {selectedId ? (
            <>
              <EmailReviewer emailId={selectedId} />
              <ThreadHistory emailId={selectedId} onSelect={selectMessage} />
              <SelectedProspectLink emailId={selectedId} />
            </>
          ) : (
            <div className="text-sm text-[var(--fg-muted)]">
              Select a message to read it, review it, or send it to agent training.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/**
 * The step from a message to the conversation it belongs to — where a held
 * reply, if the agent has composed one, is waiting for approval.
 *
 * <p>Reads the prospect off the message itself rather than the surrounding
 * list: a message opened by deep link need not appear in the current page of
 * results, and a link that silently went nowhere in exactly that case would
 * break the one journey it exists for.
 */
function SelectedProspectLink({ emailId }: { emailId: string }) {
  const detail = useQuery({
    queryKey: ["email", emailId],
    queryFn: () => api.get<EmailDetail>(`/api/v1/emails/${emailId}`),
  });
  const prospectId = detail.data?.prospectId ?? null;
  if (!prospectId) return null;
  return (
    <div className="mt-3 border-t border-[var(--border)] pt-3">
      <Link
        to={`/prospects/${prospectId}`}
        className="text-sm text-[var(--primary)] hover:underline"
      >
        Open this prospect →
      </Link>
    </div>
  );
}
